import _get from 'lodash/get';
import _pick from 'lodash/pick';

import { MULTI_FACTOR_AUTHENTICATION_FEATURE_ENABLED } from 'constants/api';
import {
    MFA_ATTEMPTS_REMAINING,
    MFA_ENROLLMENT_GENERIC_ERROR,
    MFA_ENROLLMENT_TERMINAL_ERROR_CODES,
    MFA_ERROR_TYPE_BY_CODE,
    MFA_ERROR_TYPES,
    MFA_GENERIC_ERROR,
    MFA_INVALID_CODE_ERROR,
    MFA_LOGIN_CONTEXT_KEYS,
    MFA_MAX_ATTEMPTS_ERROR,
    MFA_NETWORK_ERROR,
    MFA_REQUIRED_STATUSES,
    MFA_ROUTE_BY_STATUS,
    MFA_SERVER_ERROR_STATUS,
    MFA_SESSION_EXPIRED_ERROR,
    MFA_TERMINAL_ERROR_TYPES,
    MFA_VERIFY_ENROLLMENT_TERMINAL_ERROR_CODES,
} from 'constants/mfa';
import { convertStringToBoolean } from 'utils/common';

/**
 * Determine whether the MFA feature is enabled.
 * This lives here, and not in `utils/company`, so the auth sagas can read the
 * flag without pulling the store into a circular dependency.
 *
 * @method isMFAEnabled
 * @return {boolean}
 */
export const isMFAEnabled = () =>
    convertStringToBoolean(MULTI_FACTOR_AUTHENTICATION_FEATURE_ENABLED);

/**
 * Build the MFA requirement out of a `POST /v2/oauth/authenticate` response.
 * Returns `null` when the response is a regular (non MFA) token response.
 *
 * @method getMFARequirement
 * @param  {Object}          [response={}] The authenticate response body
 * @return {Object|null}                   The MFA requirement, or null
 */
export const getMFARequirement = (response = {}) => {
    const status = _get(response, 'status', '');
    const mfaToken = _get(response, 'mfa_token', '');

    if (!mfaToken || !MFA_REQUIRED_STATUSES.includes(status)) {
        return null;
    }

    return {
        allowedMethods: _get(response, 'allowed_methods', []),
        message: _get(response, 'message', ''),
        method: _get(response, 'mfa_method', ''),
        mfaToken,
        status,
    };
};

/**
 * Determine whether the given authenticate response requires the user
 * to complete an MFA flow before signing in.
 *
 * @method isMFARequiredResponse
 * @param  {Object}              [response={}] The authenticate response body
 * @return {boolean}
 */
export const isMFARequiredResponse = (response = {}) => Boolean(getMFARequirement(response));

/**
 * Get the MFA route for the given authenticate `status`, i.e. the verification
 * screen for an enrolled user and the setup screen for a user without a method.
 *
 * @method getMFARouteByStatus
 * @param  {string}            [status=''] The authenticate `status`
 * @return {string}                        The MFA route
 */
export const getMFARouteByStatus = (status = '') => _get(MFA_ROUTE_BY_STATUS, status, '');

/**
 * Pick the login credentials that are safe to keep around while the user
 * completes the MFA flow, so the post sign in redirect stays intact.
 *
 * @method getMFALoginContext
 * @param  {Object}           [userCreds={}] The login credentials
 * @return {Object}                          The login context
 */
export const getMFALoginContext = (userCreds = {}) => _pick(userCreds, MFA_LOGIN_CONTEXT_KEYS);

/**
 * Build the counter of the attempts left in the current MFA challenge, split
 * into the parts of the sentence so the attempts left can be emphasised.
 *
 * @method getMFAAttemptsRemainingMessage
 * @param  {number}                       remainingAttempts The attempts left
 * @return {Object}                                         The parts to render
 */
export const getMFAAttemptsRemainingMessage = remainingAttempts => {
    const template =
        remainingAttempts === 1
            ? MFA_ATTEMPTS_REMAINING.ATTEMPTS_SINGULAR
            : MFA_ATTEMPTS_REMAINING.ATTEMPTS_PLURAL;

    return {
        attempts: template.replace('ATTEMPTS_LEFT', `${remainingAttempts}`),
        prefix: MFA_ATTEMPTS_REMAINING.PREFIX,
        suffix: MFA_ATTEMPTS_REMAINING.SUFFIX,
    };
};

/**
 * Build the error state the verify identity screen renders for a failed
 * verification out of the `errorCode` of the response.
 * A rejected code (`1062`) is retryable and carries the `remainingAttempts` of
 * the challenge, an exhausted challenge or an unusable MFA session is terminal
 * and sends the user back to the login screen. A request that never reached the
 * API, or that the API answered with a server error, never got the code
 * evaluated, so it is kept apart from a rejected code. Every other code is
 * rendered with the message of the API itself, or a generic one when the
 * response carries none, and keeps the screen retryable.
 *
 * An error that never came from the API, and that carries the `mfaErrorType` it
 * stands for, is rendered as that error state rather than as a failed request,
 * i.e. a verification attempted without an MFA session reports the expired
 * session it is, and not an unreachable API.
 *
 * @method getMFAErrorState
 * @param  {Error|Object}   [error] The error of the last verification attempt
 * @return {Object}                 The error state of the screen
 */
export const getMFAErrorState = error => {
    if (!error) {
        return { errorType: '', isTerminal: false, message: '', remainingAttempts: null };
    }

    const localErrorType = _get(error, 'mfaErrorType', '');
    const response = _get(error, 'response', null);
    const status = _get(response, 'status', 0);

    if (!localErrorType && (!response || status >= MFA_SERVER_ERROR_STATUS)) {
        return {
            errorType: MFA_ERROR_TYPES.NETWORK_ERROR,
            isTerminal: false,
            message: MFA_NETWORK_ERROR,
            remainingAttempts: null,
        };
    }

    const data = _get(response, 'data', {});
    const errorCode = `${_get(data, 'errorCode', '')}`;
    const errorType = localErrorType || _get(MFA_ERROR_TYPE_BY_CODE, errorCode, '');
    const remainingAttempts = _get(data, 'remainingAttempts', null);
    const isTerminal = MFA_TERMINAL_ERROR_TYPES.includes(errorType);

    let message = _get(data, 'message', '') || MFA_GENERIC_ERROR;

    if (errorType === MFA_ERROR_TYPES.INVALID_CODE) {
        message = MFA_INVALID_CODE_ERROR;
    } else if (errorType === MFA_ERROR_TYPES.MAX_ATTEMPTS) {
        message = MFA_MAX_ATTEMPTS_ERROR;
    } else if (errorType === MFA_ERROR_TYPES.SESSION_EXPIRED) {
        message = MFA_SESSION_EXPIRED_ERROR;
    }

    return { errorType, isTerminal, message, remainingAttempts };
};

/**
 * Build the error state the verify enrollment screen renders for a failed
 * verification of the code of a started enrollment.
 *
 * The state is the one of `getMFAErrorState`, i.e. a rejected code is
 * retryable and carries the `remainingAttempts`, an exhausted challenge and an
 * unusable MFA token are terminal and a request the code was never evaluated
 * for keeps the screen as it is, with two differences of the enrollment alone:
 * a policy that no longer allows enrolling (`1069`) is terminal, the method can
 * never be activated so the user is sent back to the login screen, and a
 * rejected code is reported with the `message` of the response when the API
 * describes the failure itself.
 *
 * @method getMFAEnrollmentVerifyErrorState
 * @param  {Error|Object}                   [error] The error of the last attempt
 * @return {Object}                                 The error state of the screen
 */
export const getMFAEnrollmentVerifyErrorState = error => {
    const errorState = getMFAErrorState(error);

    if (!error || errorState.isTerminal || errorState.errorType === MFA_ERROR_TYPES.NETWORK_ERROR) {
        return errorState;
    }

    const data = _get(error, 'response.data', {});
    const errorCode = `${_get(data, 'errorCode', '')}`;

    if (MFA_VERIFY_ENROLLMENT_TERMINAL_ERROR_CODES.includes(errorCode)) {
        return {
            errorType: MFA_ERROR_TYPES.SESSION_EXPIRED,
            isTerminal: true,
            message: MFA_SESSION_EXPIRED_ERROR,
            remainingAttempts: null,
        };
    }

    return { ...errorState, message: _get(data, 'message', '') || errorState.message };
};

/**
 * Build the error state the setup screen renders for an enrollment that could
 * not be started out of the `errorCode` of the response.
 *
 * A refused enrollment, i.e. an unusable MFA token, an enrollment that is
 * already in progress, a method that is already active and a policy that no
 * longer allows enrolling, is terminal: trying again cannot get the user past
 * it. A request that never reached the API, or that the API answered with a
 * server error, is retryable and leaves the picked method untouched.
 *
 * NOTE: the text of a failure the API describes itself is the `message` of the
 * response, the front end keeps no copy of its own per error code and neither
 * translates nor rewrites it.
 *
 * @method getMFAEnrollmentErrorState
 * @param  {Error|Object}             [error] The error of the last attempt
 * @return {Object}                           The error state of the screen
 */
export const getMFAEnrollmentErrorState = error => {
    if (!error) {
        return { isTerminal: false, message: '' };
    }

    const localErrorType = _get(error, 'mfaErrorType', '');

    if (localErrorType === MFA_ERROR_TYPES.SESSION_EXPIRED) {
        return { isTerminal: true, message: MFA_SESSION_EXPIRED_ERROR };
    }

    const response = _get(error, 'response', null);
    const status = _get(response, 'status', 0);

    if (!response || status >= MFA_SERVER_ERROR_STATUS) {
        return { isTerminal: false, message: MFA_NETWORK_ERROR };
    }

    const data = _get(response, 'data', {});
    const errorCode = `${_get(data, 'errorCode', '')}`;

    return {
        isTerminal: MFA_ENROLLMENT_TERMINAL_ERROR_CODES.includes(errorCode),
        message: _get(data, 'message', '') || MFA_ENROLLMENT_GENERIC_ERROR,
    };
};
